<script setup lang="ts">
  /**
   * Activity thread — 03-components/13g-activity-thread · OSCloud DS 1.1.134
   *
   * A chronological register of entries, each written by an actor at a time. Two forms of the
   * same entry shape: a conversation (identity line over a `BfCard details` box per message)
   * and, with `log`, a history of events on a rail (tone markers, one-line summaries). Both
   * forms accept both entry kinds — a ticket thread can carry a status change between two
   * messages, and a payment log can carry a note someone typed.
   *
   * Not `BfModal`-backed message list: that is a register of records opened one at a time.
   * This is the record, already open and read top to bottom. Not a data table: no columns to
   * compare. Not `BfSectionStep`: that shows progress, this shows history.
   *
   * The entries are `BfActivityEntry`; this block is the list and its rhythm.
   */
  withDefaults(
    defineProps<{
      /** History form: a rail down the left with the marker on it, entries tight against it. */
      log?: boolean
    }>(),
    { log: false },
  )
</script>

<template>
  <ol class="activity-thread" :class="{ 'activity-thread--log': log }">
    <slot />
  </ol>
</template>

<style scoped lang="scss">
  .activity-thread {
    /* Block-local: the counterpart wash is a hook a consumer may retint, and the marker and
       its rail gap are the log form's own geometry, not spacing steps. */
    --bf-activity-thread-counterpart-surface: var(--bf-warning-surface);
    --bf-activity-thread-marker-size: 10px;
    --bf-activity-thread-rail-gap: var(--bf-sp-3);
    display: flex;
    flex-direction: column;
    gap: var(--bf-sp-3);
    margin: 0;
    padding: 0;
    list-style: none;
  }

  /* ─── Log form: a rail down the left ────────────────────────────────
     The marker sits on the rail, so the entry is a positioning context and the connecting line
     is drawn from under the marker to the entry's foot. The last entry drops the line rather
     than trailing it into empty space. */
  .activity-thread--log {
    gap: 0;
  }

  .activity-thread--log :deep(.activity-thread__entry) {
    position: relative;
    gap: var(--bf-sp-1);
    padding: 0 0 var(--bf-sp-3) calc(var(--bf-activity-thread-marker-size) + var(--bf-activity-thread-rail-gap));
  }

  .activity-thread--log :deep(.activity-thread__entry)::before {
    content: '';
    position: absolute;
    top: calc(var(--bf-activity-thread-marker-size) + 6px);
    bottom: 0;
    left: calc(var(--bf-activity-thread-marker-size) / 2 - 0.5px);
    border-left: 1px solid var(--bf-dusky-3);
  }

  .activity-thread--log :deep(.activity-thread__entry):last-child::before {
    display: none;
  }

  .activity-thread--log :deep(.activity-thread__entry):last-child {
    padding-bottom: 0;
  }

  /* The white ring lifts the dot off the rail it sits on. */
  .activity-thread--log :deep(.activity-thread__marker) {
    position: absolute;
    top: 4px;
    left: 0;
    box-shadow: 0 0 0 3px var(--bf-white);
  }

  /* Conversation form: an event entry indents to the message box's text edge (the 22px side
     padding of the details card) so the summary lines up with the messages above it. */
  .activity-thread:not(.activity-thread--log) :deep(.activity-thread__entry--event) {
    padding: 0 22px;
  }

  @media (max-width: 600px) {
    /* --bf-bp-sm */
    .activity-thread:not(.activity-thread--log) :deep(.activity-thread__entry--event) {
      padding: 0;
    }
  }
</style>
